import { ErrorBoundary } from '@sentry/react';
import { Table } from '@statsbomb/kitbag-components';
import { Atom, useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { tableColumnEntitiesAtom, tableColumnsAtom } from '@/atoms/queries/tableColumns';
import { CellValueToDisplay } from '@/components/tables/CellValueToDisplay';
import { useTableColumns } from '@/hooks/useTableColumns';
import { NestedObject } from '@/types/object';
import { DataTableColumn } from '@/types/table';
import { getFormatRule } from '@/utils/table';
import { PreviewTableState } from '../contentState/PreviewTableState';
import { GeneralErrorBoundaryFallback } from '../errorBoundary/ErrorBoundaryFallback';
import { TableWrapper } from './TableWrapper';

export const PLAYER_PREVIEW_EVENT_KEYS = [
  'event.type',
  'event.attributes.outcome',
  'competition.name',
  'game.name',
  'event.attributes.minute',
  'event.attributes.xg',
  'event.attributes.obvTotalNet',
];

export const TEAM_PREVIEW_EVENT_KEYS = [
  'event.type',
  'event.attributes.outcome',
  'playerLink',
  'competition.name',
  'game.name',
  'event.attributes.minute',
  'event.attributes.xg',
  'event.attributes.obvTotalNet',
];

export const PreviewEventsBase = ({
  eventsForMetric = [],
  eventDataTableColumns,
  isLoading = false,
  eventKeys,
}: {
  eventsForMetric?: NestedObject[];
  eventDataTableColumns?: DataTableColumn[];
  isLoading?: boolean;
  eventKeys: string[];
}) => {
  const { t } = useTranslation('events');
  const hasData = eventsForMetric.length > 0;

  return (
    <TableWrapper classes="!mt-0">
      <div className="max-h-96 overflow-auto">
        <Table withBorder={false}>
          <Table.Head>
            <Table.Row>
              {eventKeys.map((key, index) => (
                <Table.HeaderCell key={key} textAlign="left" size="regular">
                  <span data-testid={`column-${index}`}>{t(`keys.${key}`)}</span>
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {eventsForMetric.map(event => (
              <Table.Row key={String(event['event.eventId'])}>
                {eventKeys.map(columnKey => {
                  const formatRule = getFormatRule(columnKey, eventDataTableColumns);
                  const key = `${event['event.eventId']}-${columnKey}`;
                  return (
                    <CellValueToDisplay
                      key={key}
                      columnKey={columnKey}
                      value={event[columnKey]}
                      formatRule={formatRule}
                      t={t}
                      testId={key}
                    />
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <PreviewTableState isLoading={isLoading} hasData={hasData} />
      </div>
    </TableWrapper>
  );
};

const PreviewEventsWithData = ({
  eventsAtom,
  eventKeys,
}: {
  eventsAtom: Atom<Promise<NestedObject[]>>;
  eventKeys: string[];
}) => {
  useTableColumns(['event', 'event_match', 'event_player']);
  const eventsForMetric = useAtomValue(eventsAtom);
  const eventDataTableColumns = useAtomValue(tableColumnsAtom);

  return (
    <PreviewEventsBase
      eventsForMetric={eventsForMetric}
      eventDataTableColumns={eventDataTableColumns}
      eventKeys={eventKeys}
    />
  );
};

export const PreviewEvents = ({
  eventsAtom,
  eventKeys,
}: {
  eventsAtom: Atom<Promise<NestedObject[]>>;
  eventKeys: string[];
}) => (
  <ScopeProvider atoms={[tableColumnEntitiesAtom]}>
    <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
      <Suspense fallback={<PreviewEventsBase isLoading eventKeys={eventKeys} />}>
        <PreviewEventsWithData eventsAtom={eventsAtom} eventKeys={eventKeys} />
      </Suspense>
    </ErrorBoundary>
  </ScopeProvider>
);
